import { extendTailwindMerge } from 'tailwind-merge';

import {
	BACKGROUND_COLORS,
	BORDER_COLORS,
	borderRadius,
	boxShadow,
	COLORS,
	fontSize,
	getCustomThemeKeys,
	lineHeight,
	maxWidth,
	spacing,
	TEXT_COLORS,
	zIndex,
} from 'design-system';

type ClassValue =
	| ClassArray
	| string
	| number
	| bigint
	| boolean
	| null
	| undefined;
type ClassArray = ClassValue[];

function toValue(val: ClassValue): string {
	let i: number;
	let part: string;
	let result = '';

	if (typeof val === 'string') {
		result += val;
	} else if (Array.isArray(val)) {
		const len = val.length;
		for (i = 0; i < len; i++) {
			if (val[i]) {
				part = toValue(val[i]);
				if (part) {
					if (result) {
						result += ' ';
					}
					result += part;
				}
			}
		}
	}

	return result;
}

/**
 * Construct a `className` string from any number of string or arrays.
 *
 * A slightly smaller version of https://github.com/lukeed/clsx
 *
 * @example
 *
 * const variant = 'success';
 * const isDisabled = false;
 * cn(
 *   'flex',
 *   direction === 'vertical' && 'flex-col',
 *   variant === 'success' && [
 *     'bg-green',
 *     !isDisabled && 'shadow-green',
 *     isDisabled && 'border-greenLight',
 *   ],
 * );
 * // => 'flex bg-green shadow-green'
 */
export function cn(...args: ClassValue[]): string | undefined {
	let i = 0;
	let part: string;
	let result = '';
	const len = args.length;
	for (; i < len; i++) {
		if (args[i]) {
			part = toValue(args[i]);
			if (part) {
				if (result) {
					result += ' ';
				}
				result += part;
			}
		}
	}
	return result || undefined;
}

// Handle custom Tailwind theme config values.
// https://github.com/dcastil/tailwind-merge/blob/v2.6.0/docs/configuration.md
const customTwMerge = extendTailwindMerge({
	extend: {
		classGroups: {
			'font-size': [{ text: getCustomThemeKeys(fontSize) }],
			'leading': [{ leading: getCustomThemeKeys(lineHeight) }],
			'max-w': [{ 'max-w': getCustomThemeKeys(maxWidth) }],
			'shadow': [{ shadow: getCustomThemeKeys(boxShadow) }],
			'text-color': [{ text: getCustomThemeKeys(TEXT_COLORS) }],
			'z': [{ z: getCustomThemeKeys(zIndex) }],
			'border-color': getCustomThemeKeys(BORDER_COLORS),
			'rounded': getCustomThemeKeys(borderRadius),
			'gradient-from': getCustomThemeKeys(BACKGROUND_COLORS),
			'gradient-to': getCustomThemeKeys(BACKGROUND_COLORS),
			'gradient-via': getCustomThemeKeys(BACKGROUND_COLORS),
		},
		theme: {
			color: getCustomThemeKeys(COLORS),
			spacing: getCustomThemeKeys(spacing),
		},
	},
});

/**
 * Construct a `className` string from any number of string or arrays,
 * resolving conflicting Tailwind classes.
 *
 * @example
 *
 * const isLarge = true;
 * cnm('block h-4', isLarge && 'h-6')
 * // => 'block h-6'
 */
export function cnm(...args: ClassValue[]): string | undefined {
	return customTwMerge(cn(...args)) || undefined;
}
